


import { useEffect, useState } from 'react';

interface IPagination<T> {
  perPage: number | 'auto';
  data: T[];
  ref: React.RefObject<HTMLDivElement>;
  childSelector?: string;
}

const usePagination = <T>({ perPage, data, ref, childSelector }: IPagination<T>) => {
  const [page, setPage] = useState(1);
  const [currentPage, setCurrentPage] = useState<T[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [internalPerPage, setInternalPerPage] = useState(perPage === 'auto' ? 0 : perPage);

  const onNextHandler = () => {
    setPage((_page) => {
      if (perPage === 'auto') {
        const nextPageIndex = _page + internalPerPage;

        return internalPerPage + _page <= data.length ? nextPageIndex : _page;
      }
      return _page * internalPerPage <= data.length ? _page + 1 : _page;
    });
  };

  const onPrevHandler = () => {
    setPage((_page) => {
      if (perPage === 'auto') {
        const prevPageIndex = Math.max(0, _page - internalPerPage);

        return prevPageIndex;
      }
      return Math.max(1, _page - 1);
    });
  };

  const calculations = () => {
    if (ref.current) {
      // get parent height
      const parentHeight = ref.current?.clientHeight;
      // get parent scroll
      const parentScroll = ref.current?.scrollHeight;
      if (parentScroll > 0) {
        const childHeight = childSelector ? ref.current?.querySelector(childSelector)?.clientHeight || 130 : 130;

        const visibleItems = Math.floor(parentHeight / childHeight);
        setInternalPerPage(visibleItems);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (perPage === 'auto') {
        calculations();
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [perPage]);




  useEffect(() => {
    let dataPerPage = data.slice(internalPerPage * (page - 1), internalPerPage * page);
    let nextPage = internalPerPage * page;
  
    if (perPage === 'auto') {
      nextPage = internalPerPage + (page - 1);
      dataPerPage = data.slice(page - 1, nextPage); 
    }

    if (dataPerPage.length === 0 && page > 1) {
      setPage(1);
      return;
    }
  
    setHasNext(nextPage < data.length);
    setHasPrev(page > 1);
    setCurrentPage(dataPerPage);
  }, [data, internalPerPage, page, perPage]);

  useEffect(() => {
    setPage(1);
    if (perPage !== 'auto') {
      setInternalPerPage(perPage);
    }
  }, [perPage]);

  return { hasNext, hasPrev, currentPage, onNextHandler, onPrevHandler, perPage };
};

export default usePagination;
